import { usePathname, useSearchParams } from 'next/navigation';

import { SIGNUP_FORM_PATHS } from '../signup/constants';

import {
  CHECKOUT_QUERY_PARAM,
  ROUTES_WITH_CHECKOUT_HEADER,
} from '@/modules/checkout/constants';
import { VARIANT_IDENTIFIER } from '@/modules/experiments/config';
import { useExperiments } from '@/modules/experiments/useExperiments';
import { getSupportedLocations } from '@/modules/location/helpers';

const EXCLUDE_HEADER_ROUTES = [
  ...ROUTES_WITH_CHECKOUT_HEADER,
  ...SIGNUP_FORM_PATHS,
  '/picture-upload/',
  '/youtubelanding/',
  '/signup/email/magic-link/',
  '/campaign/sell/',
];

const EXCLUDE_HEADER_ROUTES_REGEX = [
  /\/login\/magic-link\/verify\//,
  /\/signup\/magic-link\/verify\//,
  /\/signup\/drop-remind\//,
  /\/drop\/remind\//,
  /^\/magic-link\/.*/,
  /^\/email-change\/.*/,
  /\/quiz\/find-your-style\//,
  /^\/(?:[a-z]{2}\/)?age-verification-(?:start|complete)\//,
  /\/collect-tax-information\/.*/,
  /^\/(?:[a-z]{2}\/)?shipping\/locations\//,
  /^\/(?:[a-z]{2}\/)?ebay\/callback\//,
];

const SIGNUP_WALL_BASE_PATHS = ['/signup/', '/login/'];
const SIGNUP_WALL_PATHS = [
  ...SIGNUP_WALL_BASE_PATHS,
  ...getSupportedLocations(
    process.env.NEXT_PUBLIC_SUPPORTED_LOCATIONS || ''
  ).flatMap((locale) =>
    SIGNUP_WALL_BASE_PATHS.map((path) => `/${locale}${path}`)
  ),
];

function excludeHeaderExactMatch(routes: string[], pathname: string) {
  return routes.includes(pathname);
}

function excludeHeaderPartialMatch(routes: RegExp[], pathname: string) {
  return routes.some((regex) => regex.test(pathname));
}

export function useHideHeader(inAppView: boolean, isMobileDevice: boolean) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [{ web_8688_signup_wall_simplified_header }] = useExperiments(
    ['web_8688_signup_wall_simplified_header'],
    { deferred: true }
  );
  const [{ web_8810_combined_signup_checkout }] = useExperiments(
    ['web_8810_combined_signup_checkout'],
    { deferred: true }
  );
  const isSimplifiedSignupWall =
    !isMobileDevice &&
    web_8688_signup_wall_simplified_header === VARIANT_IDENTIFIER;

  const isCheckoutEntry =
    searchParams.get(CHECKOUT_QUERY_PARAM) === 'true' &&
    web_8810_combined_signup_checkout === VARIANT_IDENTIFIER;

  const hideSignupWallHeader = isSimplifiedSignupWall || isCheckoutEntry;
  const excludeHeaderRoutes = hideSignupWallHeader
    ? [...EXCLUDE_HEADER_ROUTES, ...SIGNUP_WALL_PATHS]
    : EXCLUDE_HEADER_ROUTES;

  return (
    inAppView ||
    excludeHeaderExactMatch(excludeHeaderRoutes, pathname) ||
    excludeHeaderPartialMatch(EXCLUDE_HEADER_ROUTES_REGEX, pathname)
  );
}
